import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import {
  X,
  User,
  Briefcase,
  Calendar,
  Save,
  Info,
  DollarSign,
  CreditCard,
} from "lucide-react";
import { ExclamationCircleIcon as HeroExclamationCircleIcon } from "@heroicons/react/24/outline";

const Section = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border rounded-2xl mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-6 py-3 bg-gradient-to-r from-orange-50 to-orange-50 font-semibold text-gray-700 flex justify-between items-center"
      >
        <span className="text-sm text-gray-800">{title}</span>
        <span className="text-xl text-orange-500">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>}
    </div>
  );
};

// Re-usable react-select style (based on exit interview design)
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "0.75rem",
    minHeight: "44px",
    paddingLeft: "42px",
    borderColor: state.isFocused ? "#F97316" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 1px #F97316" : "none",
    "&:hover": { borderColor: "#F97316" },
  }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
  singleValue: (base) => ({ ...base, color: "#1F2937" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F97316" : state.isFocused ? "#FFEDD5" : "white",
    color: state.isSelected ? "white" : "#1F2937",
    "&:hover": { backgroundColor: "#FFEDD5", color: "#1F2937" },
  }),
  dropdownIndicator: (base) => ({ ...base, color: "#6B7280", "&:hover": { color: "#F97316" } }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "#D1D5DB" }),
  menu: (base) => ({ ...base, borderRadius: "0.75rem", border: "1px solid #E5E7EB" }),
};

export default function PayrollModal({ isOpen, onClose }) {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    staff: "",
    firstName: "",
    lastName: "",
    department: "",
    userRole: "",
    payPeriod: {
      month: "",
      year: "",
      startDate: "",
      endDate: "",
    },
    salary: {
      basic: "",
      allowances: {
        housing: "",
        transport: "",
        medical: "",
        hardship: "",
        other: "",
      },
      bonuses: {
        performance: "",
        overtime: "",
        other: "",
      },
    },
    arrears: "",
    advances: "",
    deductionDetails: {
      tax: 0,
      pension: 0,
      socialSecurity: 0,
      loan: "",
      other: "",
    },
    paymentMethod: "",
    approval: {
      status: "draft",
    },
    notes: "",
    grossPay: 0,
    totalDeductions: 0,
    netPay: 0,
  });

  const [status, setStatus] = useState({ error: "", success: "", loading: false });
  const [staffList, setStaffList] = useState([]);

  // Fetch staff when modal opens
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          "https://ims.holdthechild.net/api/staff",

          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStaffList(res.data.data || []);
      } catch (err) {
        console.error("Error fetching staff list:", err);
      }
    };
    if (isOpen) fetchStaff();
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Helper to update nested state by path like 'salary.basic' or 'deductionDetails.loan'
  const handleNestedChange = (path, value) => {
    setFormData((prev) => {
      const newFormData = JSON.parse(JSON.stringify(prev)); // simple deep clone
      const keys = path.split(".");
      let cur = newFormData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] === undefined) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return newFormData;
    });
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const parsed = type === "number" ? (value === "" ? "" : Number(value)) : value;
    handleNestedChange(name, parsed);
  };

  // payroll calculations
  useEffect(() => {
    const {
      salary: {
        basic = 0,
        allowances: {
          housing = 0,
          transport = 0,
          medical = 0,
          hardship = 0,
          other: otherAllowance = 0,
        } = {},
        bonuses: { performance = 0, overtime = 0, other: otherBonus = 0 } = {},
      } = {},
      arrears = 0,
      advances = 0,
      deductionDetails: { loan = 0, other: otherDeductions = 0 } = {},
    } = formData;

    const allAllowances = +housing + +transport + +medical + +hardship + +otherAllowance;
    const allBonuses = +performance + +overtime + +otherBonus;
    const grossPay = +basic + +allAllowances + +allBonuses + +arrears;

    const tax = grossPay * 0.15; // example 15% tax
    const pension = grossPay * 0.05;
    const socialSecurity = grossPay * 0.05;

    const totalDeductions = tax + pension + socialSecurity + +loan + +advances + +otherDeductions;
    const netPay = grossPay - totalDeductions;

    setFormData((prev) => ({
      ...prev,
      grossPay,
      totalDeductions,
      netPay,
      deductionDetails: {
        ...prev.deductionDetails,
        tax,
        pension,
        socialSecurity,
      },
    }));
  }, [
    formData.salary.basic,
    formData.salary.allowances.housing,
    formData.salary.allowances.transport,
    formData.salary.allowances.medical,
    formData.salary.allowances.hardship,
    formData.salary.allowances.other,
    formData.salary.bonuses.performance,
    formData.salary.bonuses.overtime,
    formData.salary.bonuses.other,
    formData.arrears,
    formData.advances,
    formData.deductionDetails.loan,
    formData.deductionDetails.other,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ error: "", success: "", loading: true });

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const payload = {
        staff: formData.staff,
        payPeriod: {
          ...formData.payPeriod,
          month: Number(
            [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].indexOf(formData.payPeriod.month) + 1
          ),
          year: Number(formData.payPeriod.year),
        },
        salary: {
          basic: Number(formData.salary.basic || 0),
          allowances: {
            housing: Number(formData.salary.allowances.housing || 0),
            transport: Number(formData.salary.allowances.transport || 0),
            medical: Number(formData.salary.allowances.medical || 0),
            hardship: Number(formData.salary.allowances.hardship || 0),
            other: Number(formData.salary.allowances.other || 0),
          },
          bonuses: {
            performance: Number(formData.salary.bonuses.performance || 0),
            overtime: Number(formData.salary.bonuses.overtime || 0),
            other: Number(formData.salary.bonuses.other || 0),
          },
        },
        month: formData.payPeriod.month,
        arrears: Number(formData.arrears || 0),
        advances: Number(formData.advances || 0),
        deductionDetails: {
          tax: Number(formData.deductionDetails.tax || 0),
          pension: Number(formData.deductionDetails.pension || 0),
          socialSecurity: Number(formData.deductionDetails.socialSecurity || 0),
          loan: Number(formData.deductionDetails.loan || 0),
          other: Number(formData.deductionDetails.other || 0),
        },
        grossPay: Number(formData.grossPay || 0),
        totalDeductions: Number(formData.totalDeductions || 0),
        netPay: Number(formData.netPay || 0),
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
        approval: { status: formData.approval.status },
      };

      await axios.post("https://ims.holdthechild.net/api/payroll", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setStatus({ success: "Payroll submitted successfully!", error: "", loading: false });
      onClose();
    } catch (err) {
      setStatus({
        error: err?.response?.data?.message || err.message || "Submission failed.",
        success: "",
        loading: false,
      });
    }
  };

  if (!isOpen) return null;

  const staffOptions = staffList.map((staff) => ({
    value: staff._id,
    label: `${staff.firstName} ${staff.lastName} — ${staff.role || "No Role"}`,
    department: staff.department || "",
    role: staff.role || "",
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Generate Payroll</h2>
                <p className="text-lg text-black-100 mt-1">Create and review payroll for staff</p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          {status.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <HeroExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Error</p>
                  <p className="mt-1">{status.error}</p>
                </div>
              </div>
            </div>
          )}

          {status.success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm text-green-700">
                  <p className="font-medium">Success</p>
                  <p className="mt-1">{status.success}</p>
                </div>
              </div>
            </div>
          )}

          <form id="payroll-form" onSubmit={handleSubmit} className="space-y-6">
            <Section title="Assign Staff">
              <div className="col-span-2">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Staff</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>

                  <Select
                    options={staffOptions}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        staff: selected?.value || "",
                        firstName: (selected?.label || "").split(" — ")[0]?.split(" ")[0] || "",
                        lastName: (selected?.label || "").split(" — ")[0]?.split(" ")[1] || "",
                        department: selected?.department || "",
                        userRole: selected?.role || "",
                      }))
                    }
                    value={staffOptions.find((opt) => opt.value === formData.staff) || null}
                    placeholder="Search and select staff..."
                    styles={customSelectStyles}
                    className="w-full rounded-xl"
                  />
                </div>
              </div>

              <label className="">
                Department
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="department"
                    readOnly
                    value={formData.department || ""}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                User Role
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileIconLike />
                  </div>
                  <input
                    name="userRole"
                    readOnly
                    value={formData.userRole || ""}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none capitalize"
                  />
                </div>
              </label>
            </Section>

            <Section title="Payroll Period">
              <label>
                Month <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="payPeriod.month"
                    value={formData.payPeriod.month || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  >
                    <option value="">-- Select Month --</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label>
                Year <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="payPeriod.year"
                    value={formData.payPeriod.year || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  >
                    <option value="">-- Select Year --</option>
                    {Array.from({ length: 36 }, (_, i) => 2000 + i).map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label>
                Start Date <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="payPeriod.startDate"
                    value={formData.payPeriod.startDate || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  />
                </div>
              </label>

              <label>
                End Date <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="payPeriod.endDate"
                    value={formData.payPeriod.endDate || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  />
                </div>
              </label>
            </Section>

            <Section title="Salary & Allowances">
              <label className="col-span-2">
                Basic Salary <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.basic"
                    placeholder="Basic Salary"
                    value={formData.salary.basic || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  />
                </div>
              </label>

              {/* Allowances */}
              <label>
                Housing Allowance
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.allowances.housing"
                    placeholder="Housing"
                    value={formData.salary.allowances.housing || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Transport Allowance
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.allowances.transport"
                    placeholder="Transport"
                    value={formData.salary.allowances.transport || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Medical Allowance
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.allowances.medical"
                    placeholder="Medical"
                    value={formData.salary.allowances.medical || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Hardship Allowance
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.allowances.hardship"
                    placeholder="Hardship"
                    value={formData.salary.allowances.hardship || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Other Allowance
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.allowances.other"
                    placeholder="Other Allowance"
                    value={formData.salary.allowances.other || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              {/* Bonuses & Arrears */}
              <label>
                Performance Bonus
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.bonuses.performance"
                    placeholder="Performance Bonus"
                    value={formData.salary.bonuses.performance || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Overtime Bonus
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.bonuses.overtime"
                    placeholder="Overtime Bonus"
                    value={formData.salary.bonuses.overtime || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Other Bonus
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="salary.bonuses.other"
                    placeholder="Other Bonus"
                    value={formData.salary.bonuses.other || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Arrears
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="arrears"
                    placeholder="Arrears"
                    value={formData.arrears || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>
            </Section>

            <Section title="Deductions & Summary">
              <label>
                Tax (Auto)
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="deductionDetails.tax"
                    placeholder="Tax (Auto)"
                    value={formData.deductionDetails.tax || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Pension (Auto)
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="deductionDetails.pension"
                    placeholder="Pension (Auto)"
                    value={formData.deductionDetails.pension || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Social Security (Auto)
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="deductionDetails.socialSecurity"
                    placeholder="Social Security (Auto)"
                    value={formData.deductionDetails.socialSecurity || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Loans
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="deductionDetails.loan"
                    placeholder="Loans"
                    value={formData.deductionDetails.loan || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Advances
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="advances"
                    placeholder="Advances"
                    value={formData.advances || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Other Deductions
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="deductionDetails.other"
                    placeholder="Other Deductions"
                    value={formData.deductionDetails.other || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Gross Pay
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="grossPay"
                    placeholder="Gross Pay"
                    value={formData.grossPay || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Total Deductions
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="totalDeductions"
                    placeholder="Total Deductions"
                    value={formData.totalDeductions || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label>
                Net Pay
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="netPay"
                    placeholder="Net Pay"
                    value={formData.netPay || 0}
                    readOnly
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 focus:outline-none"
                  />
                </div>
              </label>

              <label className="col-span-2">
                Payment Method <span className="text-red-500">*</span>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod || ""}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    required
                  >
                    <option value="">-- Payment Method --</option>
                    <option>Bank Transfer</option>
                    <option>Cash</option>
                    <option>Cheque</option>
                    <option>Mobile Money</option>
                  </select>
                </div>
              </label>

              <label className="col-span-2">
                Notes
                <textarea
                  name="notes"
                  rows={2}
                  placeholder="Notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  className="w-full pl-3 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                />
              </label>

              <label>
                Approval Status
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Info className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="approval.status"
                    value={formData.approval.status || ""}
                    onChange={(e) => handleNestedChange("approval.status", e.target.value)}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </label>
            </Section>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <Info className="w-4 h-4 text-orange-500" />
                <span>All fields marked as required must be completed</span>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  form="payroll-form"
                  disabled={status.loading}
                  className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${status.loading
                    ? "bg-orange-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl"
                    }`}
                >
                  {status.loading ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="w-5 h-5" />
                      <span>Submit</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Small placeholder icon component used earlier where an icon was required but not imported.
 * Replace with a proper lucide icon if you prefer.
 */
function FileIconLike() {
  return (
    <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6M7 7h8l2 2v8a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2z" />
    </svg>
  );
}

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Select from "react-select";


// const Section = ({ title, children }) => {
//   const [isOpen, setIsOpen] = useState(true);
//   return (
//     <div className="border rounded mb-4">
//       <button
//         type="button"
//         onClick={() => setIsOpen(!isOpen)}
//         className="w-full text-left px-4 py-2 bg-gray-100 font-semibold text-gray-700 flex justify-between items-center"
//       >
//         {title}
//         <span>{isOpen ? "−" : "+"}</span>
//       </button>
//       {isOpen && <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>}
//     </div>
//   );
// };

// // const formatDate = (isoString) => {
// //   const date = new Date(isoString);
// //   return date.toISOString().split("T")[0]; // only the date part
// // };

// const PayrollModal = ({ isOpen, onClose }) => {
//   const [formData, setFormData] = useState({
//     staff: "",
//     payPeriod: {
//       month: "",
//       year: "",
//       startDate: "",
//       endDate: "",
//     },
//     salary: {
//       basic: "",
//       allowances: {
//         housing: "",
//         transport: "",
//         medical: "",
//         hardship: "",
//         other: "",
//       },
//       bonuses: {
//         performance: "",
//         overtime: "",
//         other: "",
//       },
//     },
//     arrears: "",
//     advances: "",
//     deductionDetails: {
//       tax: "",
//       pension: "",
//       socialSecurity: "",
//       loan: "",
//       other: "",
//     },
//     paymentMethod: "",
//     approval: {
//       status: "Draft",
//     },
//     notes: "",
//   });
//   const [status, setStatus] = useState({ error: "", success: "", loading: false });

//   // Handle changes for nested state
//   const handleNestedChange = (path, value) => {
//     setFormData((prev) => {
//       const newFormData = { ...prev };
//       const keys = path.split(".");
//       let current = newFormData;
//       for (let i = 0; i < keys.length - 1; i++) {
//         const key = keys[i];
//         if (!current[key]) {
//           current[key] = {};
//         }
//         current = current[key];
//       }
//       current[keys[keys.length - 1]] = value;
//       return newFormData;
//     });
//   };

//   const handleChange = (e) => {
//     const { name, value, type } = e.target;
//     // Handle number conversion for number inputs
//     const parsedValue = type === "number" ? (value === "" ? "" : Number(value)) : value;
//     handleNestedChange(name, parsedValue);
//   };

//   // Effect to fetch staff info when modal opens
//   const [staffList, setStaffList] = useState([]);

//   useEffect(() => {
//     const fetchStaff = async () => {
//       try {
//         const token = localStorage.getItem("token");
//         const res = await axios.get(
//           `"https://ims.holdthechild.net"/api/staff`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         setStaffList(res.data.data || []);
//       } catch (err) {
//         console.error("Error fetching staff list:", err);
//       }
//     };
//     if (isOpen) {
//       fetchStaff();
//     }
//   }, [isOpen]);



//   // Effect to calculate payroll values whenever relevant inputs change
//   useEffect(() => {
//     const {
//       salary: {
//         basic = 0,
//         allowances: {
//           housing = 0,
//           transport = 0,
//           medical = 0,
//           hardship = 0,
//           other: otherAllowance = 0,
//         } = {},
//         bonuses: {
//           performance = 0,
//           overtime = 0,
//           other: otherBonus = 0,
//         } = {},
//       } = {},
//       arrears = 0,
//       advances = 0,
//       deductionDetails: { loan = 0, other: otherDeductions = 0 } = {},
//     } = formData;

//     const allAllowances =
//       +housing + +transport + +medical + +hardship + +otherAllowance;
//     const allBonuses = +performance + +overtime + +otherBonus;
//     const grossPay =
//       +basic + +allAllowances + +allBonuses + +arrears;

//     const tax = grossPay * 0.15; // example 15% tax
//     const pension = grossPay * 0.05;
//     const socialSecurity = grossPay * 0.05;

//     const totalDeductions = tax + pension + socialSecurity + +loan + +advances + +otherDeductions;
//     const netPay = grossPay - totalDeductions;

//     // Update form state with calculated values
//     setFormData((prev) => ({
//       ...prev,
//       grossPay,
//       totalDeductions,
//       netPay,
//       deductionDetails: {
//         ...prev.deductionDetails,
//         tax,
//         pension,
//         socialSecurity,
//       },
//     }));
//   }, [
//     formData.salary.basic,
//     formData.salary.allowances.housing,
//     formData.salary.allowances.transport,
//     formData.salary.allowances.medical,
//     formData.salary.allowances.hardship,
//     formData.salary.allowances.other,
//     formData.salary.bonuses.performance,
//     formData.salary.bonuses.overtime,
//     formData.salary.bonuses.other,
//     formData.arrears,
//     formData.advances,
//     formData.deductionDetails.loan,
//     formData.deductionDetails.other,
//   ]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setStatus({ error: "", success: "", loading: true });

//     try {
//       const token = localStorage.getItem("token");
//       if (!token) throw new Error("Authentication token missing");

//       // Construct payload to match the schema
//       const payload = {
//         staff: formData.staff,
//         payPeriod: {
//           ...formData.payPeriod,
//           month: Number(
//             [
//               "January", "February", "March", "April", "May", "June",
//               "July", "August", "September", "October", "November", "December",
//             ].indexOf(formData.payPeriod.month) + 1
//           ),
//           year: Number(formData.payPeriod.year),
//         },
//         salary: {
//           basic: Number(formData.salary.basic),
//           allowances: {
//             housing: Number(formData.salary.allowances.housing),
//             transport: Number(formData.salary.allowances.transport),
//             medical: Number(formData.salary.allowances.medical),
//             hardship: Number(formData.salary.allowances.hardship),
//             other: Number(formData.salary.allowances.other),
//           },
//           bonuses: {
//             performance: Number(formData.salary.bonuses.performance),
//             overtime: Number(formData.salary.bonuses.overtime),
//             other: Number(formData.salary.bonuses.other),
//           },
//         },
//         month: formData.payPeriod.month, // This will be the string month name
//         arrears: Number(formData.arrears),
//         advances: Number(formData.advances),
//         deductionDetails: {
//           tax: Number(formData.deductionDetails.tax),
//           pension: Number(formData.deductionDetails.pension),
//           socialSecurity: Number(formData.deductionDetails.socialSecurity),
//           loan: Number(formData.deductionDetails.loan),
//           other: Number(formData.deductionDetails.other),
//         },
//         grossPay: Number(formData.grossPay),
//         totalDeductions: Number(formData.totalDeductions),
//         netPay: Number(formData.netPay),
//         paymentMethod: formData.paymentMethod,
//         notes: formData.notes,
//         approval: {
//           status: formData.approval.status
//         }
//       };

//       await axios.post(`"https://ims.holdthechild.net"/api/payroll`, payload, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       setStatus({ success: "Payroll submitted successfully!", error: "", loading: false });
//       onClose();
//     } catch (err) {
//       setStatus({
//         error: err?.response?.data?.message || err.message || "Submission failed.",
//         success: "",
//         loading: false,
//       });
//     }
//   };

//   if (!isOpen) return null;

//   const staffOptions = staffList.map((staff) => ({
//     value: staff._id,
//     label: `${staff.firstName} ${staff.lastName} — ${staff.role || "No Role"}`,
//     department: staff.department || "",
//     role: staff.role || "",
//   }));


//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
//       <form
//         onSubmit={handleSubmit}
//         className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[95vh] overflow-y-auto relative shadow-lg"
//       >
//         <button
//           onClick={onClose}
//           type="button"
//           className="absolute top-3 right-4 text-gray-500 hover:text-black text-2xl font-bold"
//         >
//           &times;
//         </button>

//         <h2 className="text-2xl font-bold mb-4 text-gray-800">Generate Payroll</h2>

//         {status.error && <div className="bg-red-100 text-red-700 px-4 py-2 mb-4 rounded">{status.error}</div>}
//         {status.success && <div className="bg-green-100 text-green-700 px-4 py-2 mb-4 rounded">{status.success}</div>}

//         <Section title="Assign Staff">
//           <div className="col-span-2">
//             <label className="block mb-1">
//               Staff <span className="text-red-500">*</span>
//             </label>
//             <Select
//               options={staffOptions}
//               onChange={(selected) =>
//                 setFormData((prev) => ({
//                   ...prev,
//                   staff: selected?.value || "",
//                   firstName: selected?.label.split(" — ")[0].split(" ")[0] || "",
//                   lastName: selected?.label.split(" — ")[0].split(" ")[1] || "",
//                   department: selected?.department || "",
//                   userRole: selected?.role || "",
//                 }))
//               }
//               value={staffOptions.find((opt) => opt.value === formData.staff) || null}
//               placeholder="Search and select staff..."
//               className="w-full"
//             />
//           </div>

//           <label>
//             Department
//             <input
//               name="department"
//               readOnly
//               value={formData.department || ""}
//               className="w-full border rounded px-3 py-2 bg-gray-100"
//             />
//           </label>

//           <label>
//             User Role
//             <input
//               name="userRole"
//               readOnly
//               value={formData.userRole || ""}
//               className="w-full border rounded px-3 py-2 bg-gray-100 capitalize"
//             />
//           </label>
//         </Section>


//         <Section title="Payroll Period">
//           <label>
//             Month <span className="text-red-500">*</span>
//             <select name="payPeriod.month" value={formData.payPeriod.month || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required>
//               <option value="">-- Select Month --</option>
//               {[
//                 "January", "February", "March", "April", "May", "June",
//                 "July", "August", "September", "October", "November", "December"
//               ].map((month) => (
//                 <option key={month} value={month}>{month}</option>
//               ))}
//             </select>
//           </label>
//           <label>
//             Year <span className="text-red-500">*</span>
//             <select name="payPeriod.year" value={formData.payPeriod.year || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required>
//               <option value="">-- Select Year --</option>
//               {Array.from({ length: 36 }, (_, i) => 2000 + i).map((year) => (
//                 <option key={year} value={year}>{year}</option>
//               ))}
//             </select>
//           </label>
//           <label>
//             Start Date <span className="text-red-500">*</span>
//             <input type="date" name="payPeriod.startDate" value={formData.payPeriod.startDate || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
//           </label>
//           <label>
//             End Date <span className="text-red-500">*</span>
//             <input type="date" name="payPeriod.endDate" value={formData.payPeriod.endDate || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
//           </label>
//         </Section>

//         <Section title="Salary & Allowances">
//           <label>
//             Basic Salary <span className="text-red-500">*</span>
//             <input type="number" name="salary.basic" placeholder="Basic Salary" value={formData.salary.basic || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
//           </label>
//           <label>
//             Housing Allowance
//             <input type="number" name="salary.allowances.housing" placeholder="Housing" value={formData.salary.allowances.housing || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Transport Allowance
//             <input type="number" name="salary.allowances.transport" placeholder="Transport" value={formData.salary.allowances.transport || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Medical Allowance
//             <input type="number" name="salary.allowances.medical" placeholder="Medical" value={formData.salary.allowances.medical || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Hardship Allowance
//             <input type="number" name="salary.allowances.hardship" placeholder="Hardship" value={formData.salary.allowances.hardship || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Other Allowance
//             <input type="number" name="salary.allowances.other" placeholder="Other Allowance" value={formData.salary.allowances.other || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Performance Bonus
//             <input type="number" name="salary.bonuses.performance" placeholder="Performance Bonus" value={formData.salary.bonuses.performance || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Overtime Bonus
//             <input type="number" name="salary.bonuses.overtime" placeholder="Overtime Bonus" value={formData.salary.bonuses.overtime || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Other Bonus
//             <input type="number" name="salary.bonuses.other" placeholder="Other Bonus" value={formData.salary.bonuses.other || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Arrears
//             <input type="number" name="arrears" placeholder="Arrears" value={formData.arrears || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//         </Section>

//         <Section title="Deductions & Summary">
//           <label>
//             Tax (Auto)
//             <input type="number" name="deductionDetails.tax" placeholder="Tax (Auto)" value={formData.deductionDetails.tax || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Pension (Auto)
//             <input type="number" name="deductionDetails.pension" placeholder="Pension (Auto)" value={formData.deductionDetails.pension || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Social Security (Auto)
//             <input type="number" name="deductionDetails.socialSecurity" placeholder="Social Security (Auto)" value={formData.deductionDetails.socialSecurity || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Loans
//             <input type="number" name="deductionDetails.loan" placeholder="Loans" value={formData.deductionDetails.loan || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Advances
//             <input type="number" name="advances" placeholder="Advances" value={formData.advances || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Other Deductions
//             <input type="number" name="deductionDetails.other" placeholder="Other Deductions" value={formData.deductionDetails.other || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Gross Pay
//             <input type="number" name="grossPay" placeholder="Gross Pay" value={formData.grossPay || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Total Deductions
//             <input type="number" name="totalDeductions" placeholder="Total Deductions" value={formData.totalDeductions || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Net Pay
//             <input type="number" name="netPay" placeholder="Net Pay" value={formData.netPay || ""} readOnly className="w-full border rounded px-3 py-2 bg-gray-100" />
//           </label>
//           <label>
//             Payment Method <span className="text-red-500">*</span>
//             <select name="paymentMethod" value={formData.paymentMethod || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" required>
//               <option value="">-- Payment Method --</option>
//               <option>Bank Transfer</option>
//               <option>Cash</option>
//               <option>Cheque</option>
//               <option>Mobile Money</option>
//             </select>
//           </label>
//           <label>
//             Notes
//             <textarea name="notes" rows={2} placeholder="Notes" value={formData.notes || ""} onChange={handleChange} className="w-full border rounded px-3 py-2" />
//           </label>
//           <label>
//             Approval Status
//             <select name="approval.status" value={formData.approval.status || ""} onChange={handleChange} className="w-full border rounded px-3 py-2">
//               <option value="draft">Draft</option>
//               <option value="pending_approval">Pending Approval</option>
//               <option value="approved">Approved</option>
//               <option value="rejected">Rejected</option>
//             </select>
//           </label>
//         </Section>

//         <div className="flex justify-end gap-4 mt-6">
//           <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100">
//             Cancel
//           </button>
//           <button type="submit" className="px-6 py-2 bg-orange-500 text-white font-semibold rounded hover:bg-orange-600" disabled={status.loading}>
//             {status.loading ? "Submitting..." : "Submit"}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default PayrollModal;